


















import {
	computed,
	defineComponent,
	watch,
} from '@vue/composition-api';
import ErrorCard from '@front-app/components/cards/state/ErrorCard.vue';
import LoaderState from '@front-app/components/LoaderState.vue';
import { useAsyncStatus, useStatus } from '@front-app/hooks/useAsyncStatus';
import InviteCampaignForm from '@front-app/modules/group/components/admin/InviteCampaignForm.vue';
import { useAdminAccess } from '@front-app/modules/group/hooks/useAdminAccess';
import {
	CreateInvitationCampaignRequest,
	UpsertInvitationCampaignInvite,
} from '@front-app/services/GroupInvitationCampaignApi/model';
import { Group } from '@front-app/services/GroupsApi/GroupModel';
import { InvitationCampaignsStore } from '@front-app/store/singletons/InvitationCampaigns';
import { InvitationCampaignInviteesStore } from '@front-app/store/singletons/InvitationCampaignInvitees';
import { GroupsStore } from '@front-app/store/singletons/Groups';

export default defineComponent({
	components: {
		ErrorCard,
		InviteCampaignForm,
		LoaderState,
	},
	props: {
		groupId: {
			type: String,
			required: true,
		},
	},
	setup(props) {
		const group = computed(() => GroupsStore.getInstance().groupById(props.groupId));
		const { hasInviteCampaignAccess } = useAdminAccess(group);

		// Fetch the campaigns for the group
		const {
			track: trackCampaignFetch,
			isLoading: isFetchingCampaign,
		} = useAsyncStatus();

		watch(() => props.groupId, (groupId) => trackCampaignFetch(getOrCreateInvitationCampaign(groupId)), { immediate: true });

		const invitationCampaign = computed(() => {
			const existingCampaigns = InvitationCampaignsStore.groupInvitationCampaigns.value;
			return existingCampaigns.length > 0 ? existingCampaigns[0] : null;
		});
		// END Fetch the campaigns for the group

		// Fetch invitee counts
		const fetchCountsStatus = useAsyncStatus();
		const {
			isLoading: isFetchingCounts,
		} = useStatus(computed(() => fetchCountsStatus.status.value));
		watch(
			() => invitationCampaign.value?.id,
			(campaignId) => {
				if (!campaignId) {
					return;
				}
				fetchCountsStatus.track(InvitationCampaignInviteesStore.fetchInviteeCounts(
					props.groupId,
					campaignId,
				));
			},
			{ immediate: true },
		);

		const campaignInviteeCounts = computed(() => InvitationCampaignInviteesStore.inviteeCounts.value);
		// END Fetch invitee counts

		return {
			hasInviteCampaignAccess,
			// Invitation campaign
			isFetchingCampaign,
			invitationCampaign,
			// Invitee counts
			isFetchingCounts,
			campaignInviteeCounts,
		};
	},
});

function getInvitationTemplate(group: Group | undefined): UpsertInvitationCampaignInvite {
	return {
		subject: 'Invite Campaign',
		topHtml: `<p>We would like to invite you to our new online community: ${group?.name}.</p>`,
		bottomHtml: `<p>${group?.description}</p><p>Best regards, ${group?.name}</p>`,
	};
}

async function getOrCreateInvitationCampaign(groupId: string) {
	const group = GroupsStore.getInstance().groupById(groupId);

	await InvitationCampaignsStore.fetchInvitationCampaigns(groupId);
	const existingCampaigns = InvitationCampaignsStore.groupInvitationCampaigns.value;

	// Create a new campaign if there are no existing ones
	if (existingCampaigns.length === 0) {
		const defaultGroup: CreateInvitationCampaignRequest = {
			name: 'Invite Campaign',
			reminderSendHour: 0,
			templates: {
				initial: getInvitationTemplate(group),
				reminder: getInvitationTemplate(group),
			},
		};

		await InvitationCampaignsStore.createInvitationCampaign(groupId, defaultGroup);
	}
}
